/**
 * Générateur — Attendu 662 — Mesurer un angle (6ème, cluster 32).
 *
 * 6 exercices / 6 formats :
 *   - lecture_rapporteur_qcm            : QCM 4 choix + figure SVG (rapporteur
 *                                          stylisé statique) — lire la mesure
 *                                          sur un rapporteur dessiné.
 *   - mesurer_angle_saisie_rapporteur   : saisie + `rapporteur_interactif` —
 *                                          l'élève manipule un rapporteur pour
 *                                          mesurer l'angle.
 *   - mesures_canoniques_qcm            : QCM 4 choix — mesures des natures
 *                                          canoniques (droit/plat/plein/nul).
 *                                          Partition stricte vs 661 Ex.1
 *                                          (exclut aigu/obtus ; focalise sur
 *                                          nature → mesure).
 *   - comparaison_angles_qcm            : QCM 3 choix + figure SVG — comparer
 *                                          deux angles DESSINÉS (sans mesures,
 *                                          piège taille-du-dessin, option
 *                                          "égaux").
 *   - composition_angles_adj_saisie     : saisie + figure SVG — 5a somme
 *                                          xÔz = m1 + m2 ; 5b différence
 *                                          yÔz = M - m1.
 *   - estimation_vue_qcm                : QCM 4 choix + figure SVG — estimer
 *                                          à vue la mesure d'un angle.
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_662.md et docs/fiches/PAYLOADS_662.md.
 */

import {
    randomChoice, randomChoiceMultiple, pickLettresSains,
    makeQcm, makeSaisie,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 662;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_LECTURE        = "Lecture d'un rapporteur";
const L_MESURER        = 'Mesurer un angle';
const L_CANONIQUES     = 'Mesures canoniques';
const L_COMPARAISON    = 'Comparer deux angles';
const L_COMPOSITION    = "Composition d'angles adjacents";
const L_ESTIMATION     = 'Estimation à vue';

// Lettres valides — exclut I (confusion avec 1) et O (réservé au sommet xÔy).
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

// Couleurs cohérentes cluster 31→32 (cf. gen_661).
const COLOR_BLEU  = '#378ADD';
const COLOR_ROUGE = '#E24B4A';
const COLOR_GRIS  = '#666666';

// Le rapporteur est tourné EN BLOC de ±35° autour de son centre : chaque point
// décrit un cercle de ce rayon, le cadre doit donc contenir un disque complet.
// L'élément le plus éloigné n'est pas le demi-cercle (R = 110) mais les lettres
// aux extrémités, à 143,2 px du centre. D'où 292 = 2 × 146 de haut, centre à
// y = 146. Avec l'ancien cadre (300×200, centre y = 150), 37 % des tirages
// sortaient du cadre par le bas, jusqu'à 32 px.
const VIEWBOX_RAPP = { w: 300, h: 292 }; // Ex. 1 rapporteur stylisé
const VIEWBOX_EX5  = { w: 320, h: 240 }; // Ex. 5 figure 3 demi-droites
// Hauteur 240 et sommet centré : la figure tourne sur 360°, chaque élément
// décrit donc un cercle autour du sommet. Le plus éloigné est une étiquette de
// demi-droite à 100,7 px, soit 111 px en comptant la hauteur du texte. Avec
// l'ancien cadre (300×200, sommet y = 150) il ne restait que 50 px sous le
// sommet et la figure sortait massivement.
const VIEWBOX_EX6  = { w: 300, h: 240 }; // Ex. 6 angle simple

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS (alignés sur gen_661.js)
// ═══════════════════════════════════════════════════════════

/** Combining circumflex U+0302 — rend la lettre surmontée d'un chapeau. */
const chapeau = (c) => c + '\u0302';

/** Tire n lettres distinctes dans un pool (par défaut LETTRES). */
const pickN = (n, pool = LETTRES) => pickLettresSains(pool, n);

/** Sérialise une string SVG en data URL utf8. */
const svgToDataUrl = (svgString) =>
    `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;

/**
 * Construit l'attribut `d` d'un `<path>` pour un arc circulaire, de l'angle
 * `a1Deg` à `a2Deg` (convention mathématique : 0° = est, 90° = nord),
 * centré en (cx, cy), de rayon `rayon`. `sweep = 0` dessine l'arc à l'intérieur
 * de l'angle a1→a2.
 */
const arcD = (cx, cy, rayon, a1Deg, a2Deg) => {
    const a1 = (a1Deg * Math.PI) / 180;
    const a2 = (a2Deg * Math.PI) / 180;
    const x1 = cx + rayon * Math.cos(a1);
    const y1 = cy - rayon * Math.sin(a1);
    const x2 = cx + rayon * Math.cos(a2);
    const y2 = cy - rayon * Math.sin(a2);
    const deltaAbs = ((a2Deg - a1Deg) % 360 + 360) % 360;
    const largeArc = deltaAbs > 180 ? 1 : 0;
    const sweep = 0;
    return `M ${x1.toFixed(1)},${y1.toFixed(1)} A ${rayon} ${rayon} 0 ${largeArc} ${sweep} ${x2.toFixed(1)},${y2.toFixed(1)}`;
};

/** Décale `pt` de `decal` px dans la direction opposée à `vers`. */
const labelPosAt = (pt, vers, decal = 10) => {
    const dx = pt.x - vers.x;
    const dy = pt.y - vers.y;
    const n = Math.hypot(dx, dy);
    if (n < 1e-3) return { x: pt.x, y: pt.y };
    return { x: pt.x + (dx / n) * decal, y: pt.y + (dy / n) * decal };
};

/** Construit un `<text>` SVG simple (étiquette). */
const svgLabel = (text, x, y, { size = 13, italic = false, anchor = 'start', color = null } = {}) =>
    `<text x="${x.toFixed(0)}" y="${y.toFixed(0)}" font-family="serif" font-size="${size}"`
    + (italic ? ' font-style="italic"' : '')
    + (anchor !== 'start' ? ` text-anchor="${anchor}"` : '')
    + (color ? ` fill="${color}"` : '')
    + `>${text}</text>`;

/**
 * Ouvre un `<svg>` avec un attribut `data-v="{salt}"` placé AVANT `xmlns` pour
 * garantir que la variabilité tombe dans les 80 premiers caractères (fingerprint
 * auditGen).
 */
const svgOpen = (vb, salt) =>
    `<svg data-v="${salt}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}">`;

/** Salt numérique dérivé de paramètres variants (stable pour mêmes params). */
const makeSalt = (...nums) => nums.map(n => Math.round(n)).join('_');

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Lecture d'un rapporteur (QCM + figure SVG)
// ═══════════════════════════════════════════════════════════

const MESURES_EX1 = [15, 25, 35, 42, 55, 65, 75, 85, 95, 105, 115, 125, 135, 145, 155, 165];

/**
 * Construit le SVG d'un rapporteur stylisé posé sur un angle.
 *
 * Rapporteur demi-cercle centré en (150, 150), rayon 110 px. Graduations
 * extérieures 0→180 et intérieures 180→0. Double échelle visible (piège
 * pédagogique `m` vs `180-m`).
 *
 * @param {number} m - angle à mesurer (0 < m < 180)
 * @param {boolean} zeroAGauche - true si le "0" du rapporteur est à gauche
 *     (échelle intérieure correcte), false si à droite (échelle extérieure).
 * @param {number} rotationDeg - rotation globale du rapporteur (0 → orienté
 *     avec base horizontale ; non-nul → inclinaison générale).
 * @param {[string,string]} labelsExtremites - lettres aux extrémités.
 */
const buildRapporteurSvg = (m, zeroAGauche, rotationDeg, labelsExtremites) => {
    const cx = 150, cy = 146;   // centre du rapporteur = sommet de l'angle
    const R = 110;              // rayon du demi-cercle
    const salt = makeSalt(m, zeroAGauche ? 1 : 0, rotationDeg);

    // Le rapporteur est dessiné en coords locales avec base horizontale (angle 0
    // à droite, angle 180 à gauche dans la convention rapporteur). On applique
    // une rotation SVG globale `rotationDeg` AUTOUR du centre (cx, cy).
    // Convention rapporteur locale : angle θ ∈ [0, 180] en degrés anti-horaires.

    const toRad = (d) => (d * Math.PI) / 180;
    const pt = (theta) => ({ x: cx + R * Math.cos(toRad(theta)), y: cy - R * Math.sin(toRad(theta)) });

    // 1. Demi-cercle : arc de 0° à 180°
    const p0 = pt(0);
    const p180 = pt(180);
    const demiCerclePath = `M ${p0.x.toFixed(1)},${p0.y.toFixed(1)} A ${R} ${R} 0 0 0 ${p180.x.toFixed(1)},${p180.y.toFixed(1)}`;

    // 2. Ligne de base (diamètre)
    const base = `<line x1="${p180.x.toFixed(1)}" y1="${p180.y.toFixed(1)}" x2="${p0.x.toFixed(1)}" y2="${p0.y.toFixed(1)}" stroke="${COLOR_GRIS}" stroke-width="1.2"/>`;

    // 3. Graduations : traits tous les 10° (longs) + tous les 5° (courts)
    const graduations = [];
    for (let deg = 0; deg <= 180; deg += 5) {
        const isDeca = (deg % 10 === 0);
        const isVingt = (deg % 20 === 0);
        const longueur = isDeca ? (isVingt ? 10 : 7) : 4;
        const pInt = pt(deg);
        const pOut = {
            x: cx + (R - longueur) * Math.cos(toRad(deg)),
            y: cy - (R - longueur) * Math.sin(toRad(deg)),
        };
        graduations.push(
            `<line x1="${pInt.x.toFixed(1)}" y1="${pInt.y.toFixed(1)}" x2="${pOut.x.toFixed(1)}" y2="${pOut.y.toFixed(1)}" stroke="${COLOR_GRIS}" stroke-width="0.8"/>`
        );
    }

    // 4. Chiffres des graduations (tous les 20° pour éviter la surcharge)
    const chiffres = [];
    for (let deg = 0; deg <= 180; deg += 20) {
        // À 0° et 180° les nombres tombent sur la ligne de base : on les relève.
        const lift = (deg === 0 || deg === 180) ? 9 : 0;
        // Échelle extérieure : valeur = deg (lue 0→180 dans le sens anti-horaire)
        // R + 16 : au-delà de l'arc ET des graduations. À R + 10 les nombres
        // étaient collés au trait du demi-cercle.
        const pExt = {
            x: cx + (R + 16) * Math.cos(toRad(deg)),
            y: cy - (R + 16) * Math.sin(toRad(deg)),
        };
        chiffres.push(svgLabel(String(deg), pExt.x, pExt.y + 3 - lift, { size: 8, color: COLOR_GRIS, anchor: 'middle' }));
        // Échelle intérieure : valeur = 180-deg (lue 180→0). Positionnée juste
        // à l'intérieur de l'arc (R-12) pour rester côte à côte avec l'échelle
        // extérieure — matérialise visuellement le piège central (m vs 180-m).
        // R - 24 : les graduations longues descendent jusqu'à R - 10, et un
        // nombre à trois chiffres centré à R - 12 s'étendait jusqu'à R - 4 sur
        // les rayons horizontaux — il traversait les traits.
        const pInt = {
            x: cx + (R - 24) * Math.cos(toRad(deg)),
            y: cy - (R - 24) * Math.sin(toRad(deg)),
        };
        chiffres.push(svgLabel(String(180 - deg), pInt.x, pInt.y + 3 - lift, { size: 8, color: COLOR_GRIS, anchor: 'middle' }));
    }

    // 5. Deux demi-droites de l'angle.
    //    - Un côté coïncide avec le zéro du rapporteur (base).
    //    - L'autre pointe vers la graduation `m` (ou `180-m` si zéro à gauche).
    const angleCote1 = zeroAGauche ? 180 : 0;  // côté zéro
    const angleCote2 = zeroAGauche ? (180 - m) : m; // côté mesuré (graduation m lue)
    const lenDemiDroite = R + 25;
    const pCote1 = {
        x: cx + lenDemiDroite * Math.cos(toRad(angleCote1)),
        y: cy - lenDemiDroite * Math.sin(toRad(angleCote1)),
    };
    const pCote2 = {
        x: cx + lenDemiDroite * Math.cos(toRad(angleCote2)),
        y: cy - lenDemiDroite * Math.sin(toRad(angleCote2)),
    };
    const demiDroite1 = `<line x1="${cx}" y1="${cy}" x2="${pCote1.x.toFixed(1)}" y2="${pCote1.y.toFixed(1)}" stroke="black" stroke-width="1.6"/>`;
    const demiDroite2 = `<line x1="${cx}" y1="${cy}" x2="${pCote2.x.toFixed(1)}" y2="${pCote2.y.toFixed(1)}" stroke="black" stroke-width="1.6"/>`;

    // 6. Arc léger coloré sur l'angle mesuré (indicatif)
    const arcMin = Math.min(angleCote1, angleCote2);
    const arcMax = Math.max(angleCote1, angleCote2);
    const arcAngle = `<path d="${arcD(cx, cy, 22, arcMin, arcMax)}" fill="none" stroke="${COLOR_BLEU}" stroke-width="1.5" opacity="0.6"/>`;

    // 7. Sommet + étiquettes extrémités
    const sommet = `<circle cx="${cx}" cy="${cy}" r="2.5" fill="black"/>`;
    const lbl1Pos = labelPosAt(pCote1, { x: cx, y: cy }, 8);
    const lbl2Pos = labelPosAt(pCote2, { x: cx, y: cy }, 8);
    const etiq1 = svgLabel(labelsExtremites[0], lbl1Pos.x, lbl1Pos.y);
    const etiq2 = svgLabel(labelsExtremites[1], lbl2Pos.x, lbl2Pos.y);

    // 8. Assemblage avec rotation globale
    const groupe = [
        base,
        `<path d="${demiCerclePath}" fill="none" stroke="${COLOR_GRIS}" stroke-width="1.2"/>`,
        ...graduations,
        ...chiffres,
        demiDroite1,
        demiDroite2,
        arcAngle,
        sommet,
        etiq1,
        etiq2,
    ].join('');

    return svgOpen(VIEWBOX_RAPP, salt)
        + `<g transform="rotate(${rotationDeg.toFixed(1)} ${cx} ${cy})">${groupe}</g>`
        + `</svg>`;
};

const ENONCES_EX1 = [
    '<p>Quelle est la mesure de l\'angle lu sur le rapporteur ?</p>',
    '<p>D\'après la figure, combien vaut cet angle ?</p>',
];

const genLectureRapporteurQcm = () => {
    const m = randomChoice(MESURES_EX1);
    const zeroAGauche = Math.random() < 0.5;
    // Rotation globale modérée (±35°) pour varier visuellement sans désorienter.
    const rotationDeg = (Math.random() - 0.5) * 70;
    const [L1, L2] = pickN(2);

    const svg = buildRapporteurSvg(m, zeroAGauche, rotationDeg, [L1, L2]);
    const imageData = svgToDataUrl(svg);

    const texte = randomChoice(ENONCES_EX1);

    // Distracteurs : 180-m obligatoire (piège central), puis 2 autres.
    const distPool = [m + 10, m - 10, m + 20, m - 20, m + 15, m - 15]
        .filter(v => v >= 5 && v <= 175 && v !== m && v !== 180 - m);
    const autres = randomChoiceMultiple(distPool, 2);
    const distracteurs = [180 - m, ...autres];

    const choix = [`${m}°`, ...distracteurs.map(d => `${d}°`)];
    const correct = `${m}°`;

    const payload = makeQcm(choix, correct, texte, {
        ...opts(L_LECTURE),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    payload._meta.generatorKey = 'lecture_rapporteur_qcm';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — Mesurer un angle (saisie + rapporteur interactif)
// ═══════════════════════════════════════════════════════════

const MESURES_EX2 = [22, 38, 47, 56, 68, 79, 94, 108, 117, 128, 142, 156, 163];

// 4 variantes d'énoncé pour multiplier les fingerprints uniques (le
// rapporteur_interactif n'est pas hashé par auditGen).
const ENONCES_EX2 = [
    (notation) => `<p>Mesure l'angle ${notation} à l'aide du rapporteur puis saisis sa mesure en degrés.</p>`,
    (notation) => `<p>À l'aide du rapporteur ci-contre, détermine la mesure de l'angle ${notation}.</p>`,
    (notation) => `<p>Donne, en degrés, la mesure de l'angle ${notation} (utilise le rapporteur pour mesurer).</p>`,
    (notation) => `<p>Place le rapporteur sur l'angle ${notation} puis saisis sa mesure.</p>`,
];

const genMesurerAngleSaisieRapporteur = () => {
    const m = randomChoice(MESURES_EX2);
    // 100% ABĈ : 3 lettres distinctes → triplet unique quasi-garanti.
    // (Choix simple : supprime tout risque de collision sur 20 itérations.)
    const [L1, LS, L2] = pickN(3);
    const notation = L1 + chapeau(LS) + L2;

    const enonceFn = randomChoice(ENONCES_EX2);
    const texte = enonceFn(notation);

    // Rotation globale aléatoire du trait pour varier la figure.
    const baseRotation = Math.random() * 360;

    const element = {
        type: 'rapporteur_interactif',
        config: {
            // Sommet CENTRÉ et demi-droites raccourcies : l'angle de base est
            // tiré sur 360°, chaque extrémité décrit donc un cercle complet autour
            // du sommet. Avec l'ancien réglage (sommet (200, 280), rayon 200) il
            // ne restait que 140 px sous le sommet et 25 % des extrémités
            // sortaient du cadre. Ici le disque occupe 140→480 × 40→380, soit
            // 40 px de marge partout pour les lettres.
            viewBox: { w: 620, h: 420 },
            vertex: { x: 310, y: 210 },
            vertexLabel: LS,
            ray1: { angleDeg: baseRotation, labelEnd: L1 },
            ray2: { angleDeg: (baseRotation + m) % 360, labelEnd: L2 },
            rayLength: 170,
            showHintArc: true,
        },
    };

    const payload = makeSaisie(String(m), texte, {
        ...opts(L_MESURER),
        strict: true,
        suffixe: '°',
        elements_additionnels: {
            version: 1,
            elements: [element],
        },
    });
    payload._meta.generatorKey = 'mesurer_angle_saisie_rapporteur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 3 — Mesures canoniques (QCM) — nature → mesure uniquement
// ═══════════════════════════════════════════════════════════

const NATURES_CANONIQUES = ['droit', 'plat', 'plein', 'nul'];
const MESURES_CANONIQUES = { droit: 90, plat: 180, plein: 360, nul: 0 };
const POOL_DISTRACTEURS_EX3 = [0, 45, 60, 90, 120, 135, 180, 270, 360];

const genMesuresCanoniquesQcm = () => {
    const nat = randomChoice(NATURES_CANONIQUES);
    const bonne = MESURES_CANONIQUES[nat];
    // 4 variantes — TOUTES paramétrées par une lettre avec chapeau pour
    // multiplier la variété (4 natures × 4 variantes × 23 lettres = 368
    // templates × ~18 distributions de distracteurs → fingerprints ≫ 20).
    const variante = randomChoice(['V1', 'V2', 'V3', 'V4']);
    const L = randomChoice(LETTRES);
    const L_HAT = chapeau(L);

    let texte;
    if (variante === 'V1') {
        texte = `<p>L'angle ${L_HAT} est un angle ${nat}. Combien mesure-t-il en degrés ?</p>`;
    } else if (variante === 'V2') {
        texte = `<p>Sachant que ${L_HAT} est un angle ${nat}, quelle est sa mesure ?</p>`;
    } else if (variante === 'V3') {
        texte = `<p>${L_HAT} est un angle ${nat}. Donne sa mesure en degrés.</p>`;
    } else {
        texte = `<p>On sait que l'angle ${L_HAT} est ${nat}. Combien vaut-il en degrés ?</p>`;
    }

    // Distracteurs : priorité aux 3 autres mesures canoniques pour forcer le
    // contraste pédagogique, complétés par pool secondaire si nécessaire.
    const autresCanoniques = Object.entries(MESURES_CANONIQUES)
        .filter(([n, v]) => n !== nat && v !== bonne)
        .map(([, v]) => v);
    const secondaires = POOL_DISTRACTEURS_EX3.filter(v => v !== bonne && !autresCanoniques.includes(v));

    // Prendre 2 canoniques + 1 secondaire pour garantir contraste.
    const dCanoniques = randomChoiceMultiple(autresCanoniques, Math.min(2, autresCanoniques.length));
    const dSecondaire = randomChoiceMultiple(secondaires, 3 - dCanoniques.length);
    const distracteurs = [...dCanoniques, ...dSecondaire];

    const choix = [`${bonne}°`, ...distracteurs.map(d => `${d}°`)];
    const correct = `${bonne}°`;

    const payload = makeQcm(choix, correct, texte, opts(L_CANONIQUES));
    payload._meta.generatorKey = 'mesures_canoniques_qcm';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Comparaison de deux angles par leurs mesures (QCM 3 choix)
// ═══════════════════════════════════════════════════════════

const MESURES_EX4 = [18, 25, 32, 45, 52, 67, 78, 85, 95, 108, 115, 127, 135, 148, 155, 168];

// Refonte bilan prof D6 (2026-07-27) : l'ancienne version DONNAIT les deux
// mesures dans l'énoncé (« xÔy = 127° et zÔt = 95° ») — la tâche se réduisait
// à comparer deux entiers. Désormais les deux angles sont DESSINÉS, sans
// aucune mesure affichée : c'est l'ouverture qui se compare.
//   · Piège structurel (~la moitié des tirages inégaux) : le plus PETIT angle
//     est dessiné avec les côtés les plus LONGS — l'élève qui confond la
//     grandeur de l'angle avec la taille du dessin se signale. L'autre moitié
//     est neutre (mêmes longueurs), sinon « côtés longs = mauvaise réponse »
//     deviendrait la nouvelle heuristique de surface.
//   · Écart décidable à vue : |mA − mB| ≥ 30° (pas de rapporteur ici — les
//     écarts fins restent le travail des cartes lecture/mesurer).
//   · Cas « égaux » (~20 %) : même mesure, CODAGE d'égalité (trait radial
//     identique barrant chaque arc — l'élève AFFIRME l'égalité en lisant le
//     codage), côtés de longueurs franchement
//     différentes et orientations différentes — teste « l'ouverture seule
//     compte » en positif.
//   · Orientation aléatoire de chaque angle : pas d'aide par alignement.

const VIEWBOX_EX4 = { w: 620, h: 240 };
const LEN_LONG_EX4 = 110;
const LEN_COURT_EX4 = 65;

/** Un angle : deux demi-droites depuis (cx, cy), arc marqué, sommet nommé.
 *  `marque` : codage d'égalité — un trait radial identique barrant l'arc
 *  (convention des angles égaux, demandé 2026-07-28 : dans le cas « égaux »,
 *  l'élève doit pouvoir AFFIRMER l'égalité en lisant le codage, pas seulement
 *  « ne pas voir de différence »). */
const svgAngleAt = (cx, cy, m, rotDeg, len, sommet, marque = false) => {
    const toRad = (d) => (d * Math.PI) / 180;
    const ptAt = (a) => ({ x: cx + len * Math.cos(toRad(a)), y: cy - len * Math.sin(toRad(a)) });
    const P1 = ptAt(rotDeg);
    const P2 = ptAt(rotDeg + m);
    const aMid = toRad(rotDeg + m / 2);
    const tick = marque
        ? `<line x1="${(cx + 10 * Math.cos(aMid)).toFixed(1)}" y1="${(cy - 10 * Math.sin(aMid)).toFixed(1)}" `
            + `x2="${(cx + 22 * Math.cos(aMid)).toFixed(1)}" y2="${(cy - 22 * Math.sin(aMid)).toFixed(1)}" `
            + `stroke="${COLOR_BLEU}" stroke-width="2"/>`
        : '';
    return `<line x1="${cx}" y1="${cy}" x2="${P1.x.toFixed(1)}" y2="${P1.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<line x1="${cx}" y1="${cy}" x2="${P2.x.toFixed(1)}" y2="${P2.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<path d="${arcD(cx, cy, 16, rotDeg, rotDeg + m)}" fill="none" stroke="${COLOR_BLEU}" stroke-width="2"/>`
        + tick
        + `<circle cx="${cx}" cy="${cy}" r="2.5" fill="black"/>`
        // La lettre du sommet est posée À L'OPPOSÉ de l'ouverture, et non à un
        // décalage fixe en bas à droite : l'arc bleu (rayon 16) et la marque
        // d'égalité (rayon 10 à 22) occupent l'intérieur de l'angle, et la
        // rotation est tirée sur 360°. La direction opposée à la bissectrice est
        // celle qui s'éloigne le plus des deux côtés.
        + svgLabel(
            sommet,
            cx - 20 * Math.cos(aMid),
            cy + 20 * Math.sin(aMid) + 5,
            { size: 15, anchor: 'middle' },
        );
};

const buildFigureComparaisonSvg = (mA, mB, rotA, rotB, lenA, lenB, [la, lb], marque = false) => {
    const salt = makeSalt(mA, mB, rotA, rotB, lenA, lenB);
    return svgOpen(VIEWBOX_EX4, salt)
        + svgAngleAt(155, 118, mA, rotA, lenA, la, marque)
        + svgAngleAt(465, 118, mB, rotB, lenB, lb, marque)
        + '</svg>';
};

const genComparaisonAnglesQcm = () => {
    const egalite = Math.random() < 0.20;
    const mA = randomChoice(MESURES_EX4);
    let mB;
    if (egalite) {
        mB = mA;
    } else {
        // Écart ≥ 30° (relevé de 20° le 2026-07-28, retour quiz élève : à 20°
        // la différence ne saute pas toujours aux yeux sans rapporteur).
        do { mB = randomChoice(MESURES_EX4); } while (Math.abs(mB - mA) < 30);
    }

    const [LA, LB] = pickN(2);
    const rotA = Math.random() * 360;
    const rotB = Math.random() * 360;

    let lenA, lenB, regime;
    if (egalite) {
        regime = 'egaux_tailles_differentes';
        [lenA, lenB] = Math.random() < 0.5 ? [LEN_LONG_EX4, LEN_COURT_EX4] : [LEN_COURT_EX4, LEN_LONG_EX4];
    } else if (Math.random() < 0.5) {
        regime = 'piege_longueur';   // le plus petit angle a les côtés les plus longs
        if (mA < mB) { lenA = LEN_LONG_EX4; lenB = LEN_COURT_EX4; } else { lenA = LEN_COURT_EX4; lenB = LEN_LONG_EX4; }
    } else {
        regime = 'neutre';
        lenA = randomChoice([75, 85, 95]);
        lenB = lenA;
    }

    const svg = buildFigureComparaisonSvg(mA, mB, rotA, rotB, lenA, lenB, [LA, LB], egalite);
    const imageData = svgToDataUrl(svg);

    const sens = randomChoice(['plus_grand', 'plus_petit']);
    const adjectif = sens === 'plus_grand' ? 'grand' : 'petit';
    const labelA = `l'angle ${chapeau(LA)}`;
    const labelB = `l'angle ${chapeau(LB)}`;
    const choix = [labelA, labelB, 'Ils sont égaux.'];

    let correct;
    if (mA === mB) {
        correct = 'Ils sont égaux.';
    } else if (sens === 'plus_grand') {
        correct = mA > mB ? labelA : labelB;
    } else {
        correct = mA < mB ? labelA : labelB;
    }

    const texte = `<p>Observe les deux angles dessinés (aucune mesure n'est donnée). `
        + `Quel est l'angle le plus ${adjectif} ?</p>`;

    const payload = makeQcm(choix, correct, texte, {
        ...opts(L_COMPARAISON),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    payload._meta.generatorKey = 'comparaison_angles_qcm';
    payload._meta.params = { mA, mB, regime, lenA, lenB };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Composition d'angles adjacents (saisie + figure SVG)
// ═══════════════════════════════════════════════════════════

const MESURES_EX5A = [15, 22, 28, 35, 42, 48, 55, 62, 68, 75]; // m1, m2 indépendants
const TOTAUX_EX5B  = [45, 60, 75, 90, 105, 120, 135, 150];     // M = xÔz
const PETITS_EX5B  = [15, 20, 25, 30, 35, 40];                 // m1 = xÔy

const O_HAT_G = chapeau('O');
const xOy = `x${O_HAT_G}y`;
const yOz = `y${O_HAT_G}z`;
const xOz = `x${O_HAT_G}z`;

/**
 * SVG figure pour Ex. 5 : 3 demi-droites issues de O.
 * regime '5a' : 2 arcs bleu/rouge sur xÔy et yÔz (somme).
 * regime '5b' : 1 arc bleu englobant sur xÔz (total) + 1 arc rouge sur xÔy.
 */
/**
 * Rayon où poser la mesure d'un angle. Un secteur s'élargit avec le rayon :
 * sous 40° le nombre est à l'étroit près du sommet, on le repousse de 22 px.
 * `ecarter` force ensuite un écart radial minimal entre les deux mesures d'une
 * même figure, sans quoi repousser l'une la collerait à l'autre.
 */
const rayonMesure = (base, angle) => (angle < 40 ? base + 22 : base);
const ecarter = (rInterne, rExterne, mini = 24) =>
    (rExterne - rInterne < mini ? rInterne + mini : rExterne);

const buildFigureEx5Svg = (regime, param1, param2, thetaXDeg) => {
    // regime=5a : param1=m1, param2=m2 ; regime=5b : param1=M (total), param2=m1 (petit)
    const Ox = 160, Oy = 130, len = 90;
    const toRad = (d) => (d * Math.PI) / 180;

    // Angles (en coords math) des 3 demi-droites
    let dirX, dirY, dirZ;
    if (regime === '5a') {
        dirX = thetaXDeg;
        dirY = thetaXDeg + param1;
        dirZ = thetaXDeg + param1 + param2;
    } else {
        // 5b : xÔz total = param1 ; xÔy (interne) = param2
        dirX = thetaXDeg;
        dirY = thetaXDeg + param2;      // y entre x et z (car m1 < M)
        dirZ = thetaXDeg + param1;      // z au bout (M)
    }

    const ptAt = (angDeg) => ({
        x: Ox + len * Math.cos(toRad(angDeg)),
        y: Oy - len * Math.sin(toRad(angDeg)),
    });
    const X = ptAt(dirX);
    const Y = ptAt(dirY);
    const Z = ptAt(dirZ);

    const lblX = labelPosAt(X, { x: Ox, y: Oy }, 12);
    const lblY = labelPosAt(Y, { x: Ox, y: Oy }, 12);
    const lblZ = labelPosAt(Z, { x: Ox, y: Oy }, 12);

    const parts = [];

    // 3 demi-droites
    parts.push(`<line x1="${Ox}" y1="${Oy}" x2="${X.x.toFixed(1)}" y2="${X.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`);
    parts.push(`<line x1="${Ox}" y1="${Oy}" x2="${Y.x.toFixed(1)}" y2="${Y.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`);
    parts.push(`<line x1="${Ox}" y1="${Oy}" x2="${Z.x.toFixed(1)}" y2="${Z.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`);

    if (regime === '5a') {
        // Arcs bleu sur xÔy, rouge sur yÔz, avec labels de mesure
        // Chaque mesure est posée JUSTE À L'EXTÉRIEUR de son propre arc (12 px),
        // et les deux arcs sont franchement séparés (24 et 46).
        //
        // Le PLUS GRAND des deux angles prend l'arc intérieur, le plus petit
        // l'arc extérieur : un secteur étroit a besoin de rayon pour que son
        // nombre tienne sans toucher ses côtés. Les secteurs sont disjoints ici,
        // l'ordre est donc libre (contrairement au régime 5b, où xÔy est
        // emboîté dans xÔz et doit rester à l'intérieur).
        const grandDAbord = param1 >= param2;
        const rArcInt = 24, rArcExt = 46;
        const rLblInt = rayonMesure(rArcInt + 12, grandDAbord ? param1 : param2);
        const rLblExt = ecarter(rLblInt, rayonMesure(rArcExt + 12, grandDAbord ? param2 : param1));
        const rArc1 = grandDAbord ? rArcInt : rArcExt;
        const rArc2 = grandDAbord ? rArcExt : rArcInt;
        const r1 = grandDAbord ? rLblInt : rLblExt;
        const r2 = grandDAbord ? rLblExt : rLblInt;
        parts.push(`<path d="${arcD(Ox, Oy, rArc1, dirX, dirY)}" fill="none" stroke="${COLOR_BLEU}" stroke-width="2"/>`);
        parts.push(`<path d="${arcD(Ox, Oy, rArc2, dirY, dirZ)}" fill="none" stroke="${COLOR_ROUGE}" stroke-width="2"/>`);
        const angMoy1 = (dirX + dirY) / 2;
        const angMoy2 = (dirY + dirZ) / 2;
        const txt1 = { x: Ox + r1 * Math.cos(toRad(angMoy1)), y: Oy - r1 * Math.sin(toRad(angMoy1)) };
        const txt2 = { x: Ox + r2 * Math.cos(toRad(angMoy2)), y: Oy - r2 * Math.sin(toRad(angMoy2)) };
        parts.push(svgLabel(`${param1}°`, txt1.x, txt1.y, { size: 10, color: COLOR_BLEU, anchor: 'middle' }));
        parts.push(svgLabel(`${param2}°`, txt2.x, txt2.y, { size: 10, color: COLOR_ROUGE, anchor: 'middle' }));
    } else {
        // 5b : arc bleu englobant sur xÔz (rayon grand), arc rouge interne sur xÔy (rayon petit)
        // Même principe : arc interne à 22, arc total à 50, chaque mesure 12 px
        // au-delà du sien. L'interne était à 15 avec sa mesure à 25, coincée
        // entre les deux arcs et contre le sommet.
        parts.push(`<path d="${arcD(Ox, Oy, 50, dirX, dirZ)}" fill="none" stroke="${COLOR_BLEU}" stroke-width="2"/>`);
        parts.push(`<path d="${arcD(Ox, Oy, 22, dirX, dirY)}" fill="none" stroke="${COLOR_ROUGE}" stroke-width="2"/>`);
        const angMoyTot = (dirX + dirZ) / 2;
        const angMoyInt = (dirX + dirY) / 2;
        const rInt = rayonMesure(34, param2);
        const rTot = ecarter(rInt, rayonMesure(64, param1));
        const txtTot = { x: Ox + rTot * Math.cos(toRad(angMoyTot)), y: Oy - rTot * Math.sin(toRad(angMoyTot)) };
        const txtInt = { x: Ox + rInt * Math.cos(toRad(angMoyInt)), y: Oy - rInt * Math.sin(toRad(angMoyInt)) };
        parts.push(svgLabel(`${param1}°`, txtTot.x, txtTot.y, { size: 10, color: COLOR_BLEU, anchor: 'middle' }));
        parts.push(svgLabel(`${param2}°`, txtInt.x, txtInt.y, { size: 10, color: COLOR_ROUGE, anchor: 'middle' }));
    }

    // Sommet et étiquettes
    parts.push(`<circle cx="${Ox}" cy="${Oy}" r="2.5" fill="black"/>`);
    parts.push(svgLabel('x', lblX.x, lblX.y, { italic: true }));
    parts.push(svgLabel('y', lblY.x, lblY.y, { italic: true }));
    parts.push(svgLabel('z', lblZ.x, lblZ.y, { italic: true }));
    // O est posé À L'OPPOSÉ du secteur marqué, et non à un décalage fixe en bas
    // à droite : les étiquettes de mesure sont à 25 ou 34 px du sommet dans ce
    // secteur, et la figure tourne sur 360°. Le secteur ne dépasse jamais 150°,
    // la direction opposée à sa bissectrice est donc toujours libre.
    const angMoyTotal = (dirX + dirZ) / 2;
    parts.push(svgLabel(
        'O',
        Ox - 17 * Math.cos(toRad(angMoyTotal)),
        Oy + 17 * Math.sin(toRad(angMoyTotal)) + 4,
        { anchor: 'middle' },
    ));

    const salt = makeSalt(thetaXDeg, param1, param2, regime === '5a' ? 1 : 2);
    return svgOpen(VIEWBOX_EX5, salt) + parts.join('') + '</svg>';
};

const genCompositionAnglesAdjSaisie = () => {
    const regime = randomChoice(['5a', '5b']);
    const thetaXDeg = Math.random() * 360;
    let reponse, texte, svg;

    if (regime === '5a') {
        // Somme : tirer m1, m2 indépendamment, contrainte m1+m2 ≤ 150
        let m1, m2;
        do {
            m1 = randomChoice(MESURES_EX5A);
            m2 = randomChoice(MESURES_EX5A);
        } while (m1 + m2 > 150);
        reponse = m1 + m2;
        texte = `<p>Sur la figure, ${xOy} = ${m1}° et ${yOz} = ${m2}°. Quelle est la mesure de l'angle ${xOz} ?</p>`;
        svg = buildFigureEx5Svg('5a', m1, m2, thetaXDeg);
    } else {
        // Différence : tirer M (total), m1 (petit interne), contrainte m1 < M
        const M = randomChoice(TOTAUX_EX5B);
        let m1;
        // Ecart minimal de 20° : l'angle cherché est yÔz = M - m1, et à 5°
        // (M = 45, m1 = 40) il est illisible sur la figure et ses deux
        // étiquettes se recouvrent.
        do { m1 = randomChoice(PETITS_EX5B); } while (M - m1 < 20);
        reponse = M - m1;
        texte = `<p>Sur la figure, ${xOz} = ${M}° et ${xOy} = ${m1}°. Quelle est la mesure de l'angle ${yOz} ?</p>`;
        svg = buildFigureEx5Svg('5b', M, m1, thetaXDeg);
    }

    const imageData = svgToDataUrl(svg);

    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_COMPOSITION),
        strict: true,
        suffixe: '°',
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    payload._meta.generatorKey = 'composition_angles_adj_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 6 — Estimation à vue (QCM + figure SVG)
// ═══════════════════════════════════════════════════════════

const MESURES_EX6 = [20, 30, 45, 60, 75, 105, 120, 135, 150, 160];
const POOL_ENCADREMENT = [15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165];

// Fallback : triplets pré-calculés qui respectent |d−m|≥30 et |di−dj|≥30.
const DISTRACTEUR_PRESETS_EX6 = {
    20:  [[60, 105, 150], [75, 120, 165], [90, 135, 15]],
    30:  [[75, 120, 165], [90, 135, 60], [105, 150, 75]],
    45:  [[90, 135, 15], [105, 150, 75], [120, 165, 90]],
    60:  [[15, 105, 150], [30, 120, 165], [15, 120, 165]],
    75:  [[15, 120, 165], [30, 135, 105], [45, 135, 15]],
    105: [[15, 60, 135], [30, 75, 150], [45, 150, 15]],
    120: [[15, 60, 165], [30, 75, 150], [45, 90, 165]],
    135: [[15, 60, 105], [30, 75, 165], [45, 90, 165]],
    150: [[15, 60, 105], [30, 75, 120], [45, 90, 120]],
    160: [[15, 60, 105], [30, 75, 120], [45, 90, 130]],
};

/** SVG d'un angle simple pour Ex. 6 (pas de graduation, pas de mesure affichée). */
const buildFigureEx6Svg = (m, rotationDeg, labels) => {
    const Ox = 150, Oy = 120, len = 90;
    const toRad = (d) => (d * Math.PI) / 180;

    // Demi-droites à rotationDeg et rotationDeg+m (convention math)
    const dir1 = rotationDeg;
    const dir2 = rotationDeg + m;
    const ptAt = (angDeg) => ({
        x: Ox + len * Math.cos(toRad(angDeg)),
        y: Oy - len * Math.sin(toRad(angDeg)),
    });
    const P1 = ptAt(dir1);
    const P2 = ptAt(dir2);

    const lbl1 = labelPosAt(P1, { x: Ox, y: Oy }, 10);
    const lbl2 = labelPosAt(P2, { x: Ox, y: Oy }, 10);

    const salt = makeSalt(m, rotationDeg);
    return svgOpen(VIEWBOX_EX6, salt)
        + `<line x1="${Ox}" y1="${Oy}" x2="${P1.x.toFixed(1)}" y2="${P1.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<line x1="${Ox}" y1="${Oy}" x2="${P2.x.toFixed(1)}" y2="${P2.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<path d="${arcD(Ox, Oy, 18, dir1, dir2)}" fill="none" stroke="${COLOR_BLEU}" stroke-width="2"/>`
        + `<circle cx="${Ox}" cy="${Oy}" r="2.5" fill="black"/>`
        + svgLabel(labels[0], lbl1.x, lbl1.y)
        + svgLabel(labels[1], lbl2.x, lbl2.y)
        + svgLabel(labels[2], Ox + 6, Oy + 14)
        + '</svg>';
};

const genEstimationVueQcm = () => {
    const m = randomChoice(MESURES_EX6);

    // Tirage des distracteurs par rejection sampling (max 50 itérations).
    let distracteurs = null;
    const pool = POOL_ENCADREMENT.filter(v => v !== m);
    for (let attempt = 0; attempt < 50; attempt++) {
        const candidat = randomChoiceMultiple(pool, 3);
        const ok = candidat.every(d => Math.abs(d - m) >= 30)
            && candidat.every((d1, i) => candidat.every((d2, j) => i === j || Math.abs(d1 - d2) >= 30));
        if (ok) { distracteurs = candidat; break; }
    }
    // Fallback si rejection sampling échoue (rarement, mais garantit terminaison).
    if (!distracteurs) {
        const presets = DISTRACTEUR_PRESETS_EX6[m];
        distracteurs = randomChoice(presets);
    }

    const rotationDeg = Math.random() * 360;
    const [L1, L2, L3] = pickN(3);

    const svg = buildFigureEx6Svg(m, rotationDeg, [L1, L2, L3]);
    const imageData = svgToDataUrl(svg);

    const variante = randomChoice(['E1', 'E2']);
    const texte = variante === 'E1'
        ? `<p>Parmi les mesures suivantes, laquelle correspond le mieux à cet angle ?</p>`
        : `<p>Quel est l'ordre de grandeur de cet angle ?</p>`;

    const choix = [`${m}°`, ...distracteurs.map(d => `${d}°`)];
    const correct = `${m}°`;

    const payload = makeQcm(choix, correct, texte, {
        ...opts(L_ESTIMATION),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    payload._meta.generatorKey = 'estimation_vue_qcm';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    lecture_rapporteur_qcm:           genLectureRapporteurQcm,
    mesurer_angle_saisie_rapporteur:  genMesurerAngleSaisieRapporteur,
    mesures_canoniques_qcm:           genMesuresCanoniquesQcm,
    comparaison_angles_qcm:           genComparaisonAnglesQcm,
    composition_angles_adj_saisie:    genCompositionAnglesAdjSaisie,
    estimation_vue_qcm:               genEstimationVueQcm,
};

export const CATEGORIES = [
    { id: 'lecture_rapporteur',     label: L_LECTURE,     formatsList: [{ key: 'lecture_rapporteur_qcm',          label: 'QCM + figure' }] },
    { id: 'mesurer_angle',          label: L_MESURER,     formatsList: [{ key: 'mesurer_angle_saisie_rapporteur', label: 'Saisie + rapporteur interactif' }] },
    { id: 'mesures_canoniques',     label: L_CANONIQUES,  formatsList: [{ key: 'mesures_canoniques_qcm',          label: 'QCM' }] },
    { id: 'comparaison_angles',     label: L_COMPARAISON, formatsList: [{ key: 'comparaison_angles_qcm',          label: 'QCM' }] },
    { id: 'composition_angles_adj', label: L_COMPOSITION, formatsList: [{ key: 'composition_angles_adj_saisie',   label: 'Saisie + figure' }] },
    { id: 'estimation_vue',         label: L_ESTIMATION,  formatsList: [{ key: 'estimation_vue_qcm',              label: 'QCM + figure' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genLectureRapporteurQcm,
    genMesurerAngleSaisieRapporteur,
    genMesuresCanoniquesQcm,
    genComparaisonAnglesQcm,
    genCompositionAnglesAdjSaisie,
    genEstimationVueQcm,
    buildRapporteurSvg,
    buildFigureEx5Svg,
    buildFigureEx6Svg,
};
